// ============================================================
//  TASKS PAGE — ADMIN DASHBOARD
//  Path: src/pages/TasksPage.tsx
//
//  Full monitoring view: every task, who posted it, who's
//  hired (the customer/expert "pair"), category, every price
//  component, payment status, and key timestamps. Click a row
//  for the full breakdown.
//
//  Access: super_admin, admin, customer_service (view only —
//  there's no admin action on a task itself here; background
//  check approval lives on its own page).
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { listTasks, getTaskDetail, updateTask, deleteTask, getTaskPdf, getNegotiationForAdmin, getSessionNotesForAdmin, getTaskTimeline } from '../services/admin.service'
import { TimelineSection } from '../components/ActivityTimeline'
import DisputeResolvePanel from '../components/DisputeResolvePanel'
import AdminMarkDonePanel from '../components/AdminMarkDonePanel'
import { Task, Category, AdminNegotiation, SessionNote } from '../types'
import { useAuth } from '../context/AuthContext'
import api from '../config/api'

// FIX (review pass):
//  - Search no longer fires a request per keystroke (and can no longer
//    show an older search's results last): it applies 300ms after typing
//    stops, or immediately on Enter.
//  - List / detail / edit / delete / PDF failures were console-only. The
//    list said "No tasks found" and "0 total tasks"; the buttons just did
//    nothing. Each now shows its error.
//  - Clicking task A then B quickly can't show A's details.
//  - Task photos open in an in-page viewer. The old <a target=_blank>
//    link downloaded the photo (private files are served as downloads)
//    and failed outright once its 5-minute link expired.
//  - PDFs: same Safari pop-up / Firefox-Safari download fixes as
//    ReportsPage.tsx, and the server's error message is read from the
//    blob response.
//  - 'full' / 'expired' task statuses and the 'refunding' payment status
//    exist in the schema but had no filter button or colour.

const TASK_STATUS_COLORS: Record<string, string> = {
  open:               'bg-blue-600/20 text-blue-400',
  full:               'bg-indigo-600/20 text-indigo-400',
  assigned:           'bg-yellow-600/20 text-yellow-400',
  pending_completion: 'bg-orange-600/20 text-orange-400',
  completed:          'bg-green-600/20 text-green-400',
  disputed:           'bg-red-600/20 text-red-400',
  cancelled:          'bg-gray-600/20 text-gray-400',
  expired:            'bg-gray-600/20 text-gray-500',
}

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  pending:             'bg-gray-600/20 text-gray-400',
  // authorized = the card is only HELD (not charged yet); capturing =
  // being charged right now; releasing = the hold is being released.
  authorized:          'bg-blue-600/20 text-blue-400',
  capturing:           'bg-yellow-600/20 text-yellow-400',
  releasing:           'bg-yellow-600/20 text-yellow-400',
  paid:                'bg-green-600/20 text-green-400',
  refunding:           'bg-yellow-600/20 text-yellow-400',
  refunded:            'bg-red-600/20 text-red-400',
  partially_refunded:  'bg-orange-600/20 text-orange-400',
  failed:              'bg-red-600/20 text-red-400',
}

// Words an admin should see for each payment status.
const PAYMENT_STATUS_LABELS: Record<string, string> = {
  authorized: 'on hold',
  capturing:  'charging',
  releasing:  'releasing hold',
}
const paymentLabel = (s: string | null | undefined) =>
  PAYMENT_STATUS_LABELS[s || ''] || String(s || '').replace(/_/g, ' ')

const STATUS_FILTERS = ['', 'open', 'full', 'assigned', 'pending_completion', 'completed', 'disputed', 'cancelled', 'expired']

// Rounds to cents the same way the backend's own round2 does — used only
// to recover the commission figure client-side (it isn't a stored
// column; see the note where it's computed in the Payment detail panel).
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

// Module-level so hooks that use them don't gain dependencies (a CI=true
// build treats react-hooks/exhaustive-deps warnings as errors).
const errorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message || (err?.isNetworkError
    ? 'Could not reach the server — check your connection and try again.'
    : fallback)

// PDFs are fetched with responseType 'blob', so an error body is a Blob.
const pdfErrorMessage = async (err: any): Promise<string> => {
  if (err?.isNetworkError) return 'Could not reach the server — check your connection and try again.'
  const data = err?.response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text())
      if (parsed?.message) return parsed.message
    } catch { /* not JSON */ }
  }
  return 'Could not create the PDF — try again.'
}

const TasksPage: React.FC = () => {
  const { user } = useAuth()
  const isSuperAdmin = user?.roles.includes('super_admin')
  // Resolving a dispute moves money — super_admin / admin only (same as
  // the backend route); customer_service sees the dispute read-only.
  const canResolveDisputes = !!(user?.roles.includes('super_admin') || user?.roles.includes('admin'))

  const [tasks, setTasks]               = useState<Task[]>([])
  const [total, setTotal]                = useState(0)
  const [page, setPage]                  = useState(1)
  // searchInput = what's typed; search = what's actually queried.
  const [searchInput, setSearchInput]    = useState('')
  const [search, setSearch]              = useState('')
  const [listError, setListError]        = useState('')
  const [detailError, setDetailError]    = useState('')
  const [actionError, setActionError]    = useState('')
  const [pdfBusy, setPdfBusy]            = useState<'view' | 'download' | null>(null)
  const [previewPhoto, setPreviewPhoto]  = useState<{ fileUrl: string; fileName: string } | null>(null)
  const listSeq   = useRef(0)
  const detailSeq = useRef(0)
  // The status filter lives in the URL (?status=disputed) so links can
  // open this page already filtered — the sidebar's "Disputes" item and
  // the dashboard's disputes card both point at /tasks?status=disputed.
  // Only known statuses are accepted from the URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFromUrl = () => {
    const s = searchParams.get('status') || ''
    return STATUS_FILTERS.includes(s) ? s : ''
  }
  const [statusFilter, setStatusFilter]  = useState(statusFromUrl)
  // Follow the URL when it changes while this page is open (e.g. the
  // sidebar's "Disputes" clicked while already on Tasks).
  useEffect(() => {
    const s = statusFromUrl()
    if (s !== statusFilter) { setStatusFilter(s); setPage(1) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  const chooseStatus = (s: string) => {
    setStatusFilter(s)
    setPage(1)
    const next = new URLSearchParams(searchParams)
    if (s) next.set('status', s); else next.delete('status')
    setSearchParams(next, { replace: true })
  }
  const [categoryId, setCategoryId]      = useState('')
  const [categories, setCategories]      = useState<Category[]>([])
  const [loading, setLoading]            = useState(true)
  const [selectedTask, setSelectedTask]  = useState<Task | null>(null)
  const [negotiation, setNegotiation]    = useState<AdminNegotiation | null>(null)
  const [sessionNotes, setSessionNotes]  = useState<SessionNote[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [editMode, setEditMode]           = useState(false)
  const [editForm, setEditForm]           = useState({ title: '', description: '', area: '', address: '', budget: '' })
  const [saving, setSaving]               = useState(false)
  const [deleting, setDeleting]           = useState(false)

  const fetchTasks = useCallback(async () => {
    const seq = ++listSeq.current
    setLoading(true)
    setListError('')
    try {
      const res = await listTasks({
        page, limit: 20,
        search: search || undefined,
        status: statusFilter || undefined,
        categoryId: categoryId || undefined,
      })
      if (seq !== listSeq.current) return // a newer search/filter superseded this one
      setTasks(res.data.data)
      setTotal(res.data.pagination.total)
    } catch (err) {
      if (seq !== listSeq.current) return
      console.error('Failed to fetch tasks:', (err as any)?.response?.status, (err as any)?.message)
      setTasks([])
      setTotal(0)
      setListError(errorMessage(err, 'Failed to load tasks.'))
    } finally {
      if (seq === listSeq.current) setLoading(false)
    }
  }, [page, search, statusFilter, categoryId])

  useEffect(() => {
    api.get('/api/categories').then((res) => setCategories(res.data.data)).catch((err) => console.error('Failed to fetch categories:', (err as any)?.response?.status, (err as any)?.message))
  }, [])

  useEffect(() => { fetchTasks() }, [fetchTasks])

  // Apply the typed search 300ms after typing stops.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // Enter applies it immediately.
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput.trim())
    setPage(1)
  }

  const openDetail = async (taskId: string) => {
    const seq = ++detailSeq.current
    setDetailLoading(true)
    setDetailError('')
    setActionError('')
    setEditMode(false)
    setNegotiation(null)
    setSessionNotes([])
    try {
      const res = await getTaskDetail(taskId)
      if (seq !== detailSeq.current) return // another task was clicked since
      setSelectedTask(res.data.data)

      const relevantApp = res.data.data.applications?.find(
        (a: any) => ['hired', 'completed', 'shortlisted'].includes(a.status)
      )
      if (relevantApp) {
        try {
          const negRes = await getNegotiationForAdmin(relevantApp.id)
          if (seq === detailSeq.current && negRes.data.data.offers.length > 0) setNegotiation(negRes.data.data)
        } catch (negErr) {
          console.error('Failed to fetch negotiation history:', (negErr as any)?.response?.status, (negErr as any)?.message)
        }
        try {
          const notesRes = await getSessionNotesForAdmin(relevantApp.id)
          if (seq === detailSeq.current) setSessionNotes(notesRes.data.data.notes)
        } catch (notesErr) {
          console.error('Failed to fetch session notes:', (notesErr as any)?.response?.status, (notesErr as any)?.message)
        }
      }
    } catch (err) {
      if (seq !== detailSeq.current) return
      console.error('Failed to fetch task detail:', (err as any)?.response?.status, (err as any)?.message)
      setDetailError(errorMessage(err, 'Could not open this task.'))
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false)
    }
  }

  const startEdit = () => {
    if (!selectedTask) return
    setEditForm({
      title:       selectedTask.title || '',
      description: selectedTask.description || '',
      area:        selectedTask.area || '',
      address:     selectedTask.address || '',
      budget:      selectedTask.budget != null ? String(selectedTask.budget) : '',
    })
    setEditMode(true)
  }

  const handleSaveEdit = async () => {
    if (!selectedTask) return
    setSaving(true)
    setActionError('')
    try {
      await updateTask(selectedTask.id, {
        title: editForm.title,
        description: editForm.description,
        area: editForm.area,
        address: editForm.address,
        budget: editForm.budget ? Number(editForm.budget) : undefined,
      })
      await openDetail(selectedTask.id)
      await fetchTasks()
      setEditMode(false)
    } catch (err) {
      console.error('Failed to update task:', (err as any)?.response?.status, (err as any)?.message)
      setActionError(errorMessage(err, 'Failed to save changes — try again.'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTask = async () => {
    if (!selectedTask) return
    if (!window.confirm(`Permanently delete "${selectedTask.title}"? This cannot be undone.`)) return
    setDeleting(true)
    setActionError('')
    try {
      await deleteTask(selectedTask.id)
      setSelectedTask(null)
      await fetchTasks()
    } catch (err) {
      console.error('Failed to delete task:', (err as any)?.response?.status, (err as any)?.message)
      setActionError(errorMessage(err, 'Failed to delete this task — try again.'))
    } finally {
      setDeleting(false)
    }
  }

  const fetchTaskPdfUrl = async (taskId: string) => {
    const res = await getTaskPdf(taskId)
    return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
  }

  const handleViewTaskPdf = async () => {
    if (!selectedTask) return
    setActionError('')
    // Opened inside the click so it isn't blocked as a pop-up; filled in
    // once the PDF arrives.
    const tab = window.open('', '_blank')
    setPdfBusy('view')
    try {
      const blobUrl = await fetchTaskPdfUrl(selectedTask.id)
      if (tab) tab.location.href = blobUrl
      else window.location.assign(blobUrl)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (err) {
      console.error('Failed to load task PDF:', (err as any)?.response?.status, (err as any)?.message)
      tab?.close()
      setActionError(await pdfErrorMessage(err))
    } finally {
      setPdfBusy(null)
    }
  }

  const handleDownloadTaskPdf = async () => {
    if (!selectedTask) return
    setActionError('')
    setPdfBusy('download')
    try {
      const blobUrl = await fetchTaskPdfUrl(selectedTask.id)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `task-${selectedTask.id}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      // Revoking right after click() can cancel the download in Firefox/Safari.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
    } catch (err) {
      console.error('Failed to download task PDF:', (err as any)?.response?.status, (err as any)?.message)
      setActionError(await pdfErrorMessage(err))
    } finally {
      setPdfBusy(null)
    }
  }

  const hiredApplication = selectedTask?.applications?.find(
    (a) => a.status === 'hired' || a.status === 'completed'
  )

  const formatDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

  const totalPages = Math.ceil(total / 20)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Tasks</h1>
          <p className="text-gray-400 text-sm mt-1">{listError ? '—' : `${total} total tasks`}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1 min-w-[250px]">
          <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by task title, customer, or expert name..."
            className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
        </form>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map((s) => (
            <button key={s} onClick={() => chooseStatus(s)}
              className={`px-4 py-2 text-sm rounded-xl transition-colors ${
                statusFilter === s ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-gray-400 hover:text-white'
              }`}>{s ? s.replace('_', ' ') : 'All'}</button>
          ))}
        </div>
        <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(1) }}
          className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none">
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {listError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{listError}</p>
          <button onClick={() => fetchTasks()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      )}
      {detailError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {detailError}
          <button onClick={() => setDetailError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Table */}
        <div className={`bg-surface-card border border-border-light rounded-2xl overflow-hidden ${selectedTask ? 'flex-1' : 'w-full'}`}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-light">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Task</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Customer</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Price</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Payment</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Posted</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : listError ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">—</td></tr>
              ) : tasks.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400">No tasks found</td></tr>
              ) : (
                tasks.map((t) => {
                  const hired = t.applications?.find((a) => a.status === 'hired' || a.status === 'completed')
                  return (
                    <tr key={t.id} onClick={() => openDetail(t.id)}
                      className={`border-b border-border-light cursor-pointer transition-colors ${
                        selectedTask?.id === t.id ? 'bg-brand-600/10' : 'hover:bg-surface-tertiary/50'
                      }`}>
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-white max-w-xs truncate">{t.title}</p>
                        <p className="text-xs text-gray-500">
                          {t.category?.name}
                          {t.category?.isTwoLocation && (
                            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-600/20 text-blue-400">
                              {t.category?.requiresPickupCode ? '🔢 Code' : '📍 2-Loc'}
                            </span>
                          )}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-gray-300">{t.customer?.user.firstName} {t.customer?.user.lastName}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-gray-300">
                          {hired ? `${hired.expert?.user.firstName} ${hired.expert?.user.lastName}` : <span className="text-gray-600">Not hired</span>}
                        </p>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-300">
                        {t.totalCharged != null ? `$${t.totalCharged.toFixed(2)}` : t.budget != null ? `~$${t.budget}` : '—'}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${TASK_STATUS_COLORS[t.status] || 'bg-gray-600/20 text-gray-400'}`}>
                          {t.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${PAYMENT_STATUS_COLORS[t.paymentStatus] || 'bg-gray-600/20 text-gray-400'}`}>
                          {paymentLabel(t.paymentStatus)}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-400">{new Date(t.createdAt).toLocaleDateString()}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
              <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Previous</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Next</button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedTask && (
          <div className="w-96 bg-surface-card border border-border-light rounded-2xl p-5 h-fit sticky top-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Task Details</h3>
              <button onClick={() => setSelectedTask(null)} className="text-gray-500 hover:text-white text-lg">✕</button>
            </div>

            {actionError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-xs">{actionError}</p>
              </div>
            )}

            {detailLoading ? (
              <p className="text-gray-400 text-sm">Loading...</p>
            ) : editMode ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Title</label>
                  <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-brand-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Description</label>
                  <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3}
                    className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-brand-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Area</label>
                  <input value={editForm.area} onChange={(e) => setEditForm({ ...editForm, area: e.target.value })}
                    className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-brand-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Full Address</label>
                  <input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-brand-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Budget</label>
                  <input type="number" value={editForm.budget} onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })}
                    className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-brand-500 focus:outline-none" />
                </div>
                <p className="text-xs text-gray-600">Note: payment fields can't be edited here — only editable through the real payment flows.</p>
                <div className="flex gap-2">
                  <button onClick={() => setEditMode(false)} className="flex-1 px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
                  <button onClick={handleSaveEdit} disabled={saving} className="flex-1 px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-base font-semibold text-white mb-1">{selectedTask.title}</p>
                <p className="text-xs text-gray-500 mb-4">{selectedTask.category?.name} · {selectedTask.urgencyLevel?.name}</p>

                <span className={`inline-block mb-4 px-2 py-1 text-xs rounded-full ${TASK_STATUS_COLORS[selectedTask.status] || 'bg-gray-600/20 text-gray-400'}`}>
                  {selectedTask.status.replace('_', ' ')}
                </span>

                <div className="flex gap-2 mb-4">
                  <button onClick={handleViewTaskPdf} disabled={pdfBusy !== null}
                    className="flex-1 px-3 py-2 text-xs bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light disabled:opacity-50 transition-colors">
                    {pdfBusy === 'view' ? 'Preparing…' : '📄 View PDF'}
                  </button>
                  <button onClick={handleDownloadTaskPdf} disabled={pdfBusy !== null}
                    className="flex-1 px-3 py-2 text-xs bg-brand-600/10 text-brand-400 rounded-xl hover:bg-brand-600/20 disabled:opacity-50 transition-colors">
                    {pdfBusy === 'download' ? 'Preparing…' : '⬇ Download PDF'}
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase mb-2">Pair</p>
                  <DetailRow label="Customer" value={`${selectedTask.customer?.user.firstName} ${selectedTask.customer?.user.lastName}`} />
                  <DetailRow label="Customer Contact" value={selectedTask.customer?.user.phone || selectedTask.customer?.user.email || '—'} />
                  {hiredApplication ? (
                    <>
                      <DetailRow label="Hired Expert" value={`${hiredApplication.expert?.user.firstName} ${hiredApplication.expert?.user.lastName}`} />
                      <DetailRow label="Expert Contact" value={hiredApplication.expert?.user.phone || hiredApplication.expert?.user.email || '—'} />
                    </>
                  ) : (
                    <DetailRow label="Hired Expert" value="Not hired yet" />
                  )}
                </div>

                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase mb-2">Location</p>
                  <DetailRow label="Area" value={selectedTask.area || '—'} />
                  <DetailRow label="Location Type" value={
                    selectedTask.locationType === 'expert_location' ? "Tasker's location" :
                    selectedTask.locationType === 'virtual'          ? 'Virtual' :
                    "Customer's address"
                  } />
                  <DetailRow label="Full Address" value={selectedTask.displayAddress || selectedTask.address || '—'} />
                </div>

                {/* Two-location / pickup code section */}
                {selectedTask.category?.isTwoLocation && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 uppercase mb-2">Pickup Details</p>
                    <DetailRow label="Pickup Location" value={selectedTask.restaurantName || '—'} />
                    <DetailRow label="Pickup Address"  value={selectedTask.restaurantAddress || '—'} />
                    {selectedTask.deliveryFee != null && (
                      <DetailRow label="Delivery Fee" value={`$${selectedTask.deliveryFee.toFixed(2)}`} />
                    )}
                    {selectedTask.category?.requiresPickupCode && (
                      <div className="mt-2 p-3 bg-surface-tertiary rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">Pickup Code (visible to admin for support)</p>
                        <p className="text-2xl font-bold text-brand-400 tracking-widest">
                          {selectedTask.pickupCode || '—'}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Order confirmed by customer: {selectedTask.orderConfirmed ? '✅ Yes' : '⚠️ Not confirmed'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 uppercase mb-2">Photos</p>
                    {(() => {
                      const taskPhotos = selectedTask.attachments!.filter((a) => a.type === 'task_photo')
                      const completionPhotos = selectedTask.attachments!.filter((a) => a.type === 'completion_photo')
                      const disputePhotos = selectedTask.attachments!.filter((a) => a.type === 'dispute_photo')
                      return (
                        <>
                          {taskPhotos.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-600 mb-1.5">Task photos (from customer)</p>
                              <div className="flex gap-2 flex-wrap">
                                {taskPhotos.map((photo) => (
                                  <button key={photo.id} type="button" onClick={() => setPreviewPhoto({ fileUrl: photo.fileUrl, fileName: photo.fileName })}>
                                    <img src={photo.fileUrl} alt={photo.fileName} className="w-16 h-16 rounded-lg object-cover border border-border-light hover:border-brand-500 transition-colors" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {completionPhotos.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-600 mb-1.5">Completion photos (from tasker) — reference for finished work</p>
                              <div className="flex gap-2 flex-wrap">
                                {completionPhotos.map((photo) => (
                                  <button key={photo.id} type="button" onClick={() => setPreviewPhoto({ fileUrl: photo.fileUrl, fileName: photo.fileName })}>
                                    <img src={photo.fileUrl} alt={photo.fileName} className="w-16 h-16 rounded-lg object-cover border border-border-light hover:border-brand-500 transition-colors" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {disputePhotos.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1.5">Photos from the customer's issue report — evidence for the decision</p>
                              <div className="flex gap-2 flex-wrap">
                                {disputePhotos.map((photo) => (
                                  <button key={photo.id} type="button" onClick={() => setPreviewPhoto({ fileUrl: photo.fileUrl, fileName: photo.fileName })}>
                                    <img src={photo.fileUrl} alt={photo.fileName} className="w-16 h-16 rounded-lg object-cover border border-red-500/40 hover:border-brand-500 transition-colors" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </div>
                )}

                {selectedTask.reviews && selectedTask.reviews.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 uppercase mb-2">Ratings</p>
                    <div className="space-y-2">
                      {selectedTask.reviews.map((review) => (
                        <div key={review.id} className="bg-surface-tertiary rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-400">
                              {review.fromUser.firstName} {review.fromUser.lastName} <span className="text-gray-600">({review.fromRole})</span>
                            </span>
                            <span className="text-yellow-400 text-xs">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</span>
                          </div>
                          {review.comment && <p className="text-xs text-gray-300">{review.comment}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase mb-2">Payment</p>
                  <span className={`inline-block mb-2 px-2 py-1 text-xs rounded-full ${PAYMENT_STATUS_COLORS[selectedTask.paymentStatus] || 'bg-gray-600/20 text-gray-400'}`}>
                    {paymentLabel(selectedTask.paymentStatus)}
                  </span>
                  {selectedTask.paymentStatus === 'authorized' && (
                    <p className="text-xs text-blue-300 mb-2">
                      The customer’s card is only held — it is charged when the job is confirmed. If the customer cancels: 24h+ before the start the hold is released, 3–24h before 50% is charged, under 3h the full amount is charged (tasker paid).
                    </p>
                  )}
                  <DetailRow label="Base Price" value={selectedTask.finalPrice != null ? `$${selectedTask.finalPrice.toFixed(2)}` : '—'} />

                  {/* FIX (production-readiness pass, resolved): this panel used
                      to show only Base Price / Tax / Total Charged because
                      getTaskDetail's response didn't carry the fee breakdown
                      yet. It does now — every customer- and tasker-side fee
                      field is a real column on Task, locked in at hire time by
                      computeHireBreakdown (payment.service.js), and
                      getTaskDetail's unrestricted `include` already returns
                      all of them. C/T = Customer / Tasker — the actual split
                      per the category's fee settings, not always 100/0. Same
                      pattern as PaymentsPage.tsx's Recent Transactions table
                      and Customer/ExpertDetailPage.tsx's breakdowns, so this
                      panel now agrees with those instead of showing less. */}
                  <DetailRow label="Insurance (C/T)" value={`$${(selectedTask.insuranceFee ?? 0).toFixed(2)} / $${(selectedTask.insuranceFeeTasker ?? 0).toFixed(2)}`} />
                  <DetailRow label="Service Fee (C/T)" value={`$${(selectedTask.serviceFee ?? 0).toFixed(2)} / $${(selectedTask.serviceFeeTasker ?? 0).toFixed(2)}`} />
                  <DetailRow label="Processing (C/T)" value={`$${(selectedTask.processingFee ?? 0).toFixed(2)} / $${(selectedTask.processingFeeTasker ?? 0).toFixed(2)}`} />
                  <DetailRow label="Tax (customer)" value={selectedTask.taxAmount != null ? `$${selectedTask.taxAmount.toFixed(2)}` : '—'} />
                  <DetailRow label={selectedTask.paymentStatus === 'authorized' ? 'Total On Hold (Customer)' : 'Total Charged (Customer)'} value={selectedTask.totalCharged != null ? `$${selectedTask.totalCharged.toFixed(2)}` : '—'} />

                  {/* Expert-side payout. taskerPayout is the SAME column the
                      expert's own earnings screen and the database read
                      (payment.service.js's getExpertEarnings) — not a
                      client-side recomputation — so this figure can't drift
                      from what the expert actually sees or actually gets
                      paid. commission is recovered via the identity
                      taskerPayout's own formula guarantees (finalPrice -
                      commission - tasker fees = taskerPayout), same as
                      admin.service.js's splitTaskRevenue. Only shown once
                      the task has an actual price — nothing to break down
                      before that. */}
                  {selectedTask.finalPrice != null && (
                    <>
                      <p className="text-xs text-gray-500 uppercase mt-3 mb-2">Expert Payout</p>
                      {selectedTask.taskerPayout != null ? (
                        <>
                          <DetailRow label="Commission" value={`-$${round2(
                            selectedTask.finalPrice
                            + (selectedTask.taxOnJob ?? 0)
                            - selectedTask.taskerPayout
                            - (selectedTask.insuranceFeeTasker ?? 0)
                            - (selectedTask.processingFeeTasker ?? 0)
                            - (selectedTask.serviceFeeTasker ?? 0)
                            - (selectedTask.taxFeeTasker ?? 0)
                          ).toFixed(2)}`} />
                          <DetailRow label="Insurance (Expert)" value={`-$${(selectedTask.insuranceFeeTasker ?? 0).toFixed(2)}`} />
                          <DetailRow label="Service Fee (Expert)" value={`-$${(selectedTask.serviceFeeTasker ?? 0).toFixed(2)}`} />
                          <DetailRow label="Processing Fee (Expert)" value={`-$${(selectedTask.processingFeeTasker ?? 0).toFixed(2)}`} />
                          {(selectedTask.taxOnJob ?? 0) > 0 && (
                            <DetailRow label="Tax on job price (customer paid, expert remits)" value={`+$${(selectedTask.taxOnJob ?? 0).toFixed(2)}`} />
                          )}
                          {(selectedTask.taxFeeTasker ?? 0) > 0 && (
                            <DetailRow label="Tax on fees (Expert)" value={`-$${(selectedTask.taxFeeTasker ?? 0).toFixed(2)}`} />
                          )}
                          {(selectedTask as any).taxJurisdictionLabel && (
                            <DetailRow label="Tax used" value={`${(selectedTask as any).taxJurisdictionLabel}${(selectedTask as any).taxProvinceApplied ? ` · ${(selectedTask as any).taxProvinceApplied}` : ''}${(selectedTask as any).taxExpertRegisteredApplied != null ? ` · tasker ${(selectedTask as any).taxExpertRegisteredApplied ? 'registered' : 'not registered'}` : ''}${(selectedTask as any).taxPricingModeApplied === 'TAX_INCLUDED' ? ` · tax included in price ($${Number((selectedTask as any).taxIncludedInJob || 0).toFixed(2)})` : (selectedTask as any).taxPricingModeApplied === 'TAX_ADDED' ? ' · tax added to price' : ''}`} />
                          )}
                          {Array.isArray((selectedTask as any).taxLedger) && (selectedTask as any).taxLedger.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-gray-500 uppercase mb-2">Tax record (permanent)</p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs text-gray-300">
                                  <thead>
                                    <tr className="text-gray-500 text-left">
                                      <th className="pr-2 py-1">Item</th><th className="pr-2">Base</th><th className="pr-2">Rate</th><th className="pr-2">Tax</th><th className="pr-2">Borne by</th><th className="pr-2">Remitted by</th><th>Where</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(selectedTask as any).taxLedger.map((l: any) => (
                                      <tr key={l.id} className="border-t border-gray-700/40">
                                        <td className="pr-2 py-1">{String(l.component).replace(/_/g, ' ').toLowerCase()}{l.entryType === 'ADJUSTMENT' ? ' (adjustment)' : ''}</td>
                                        <td className="pr-2">${Number(l.baseAmount).toFixed(2)}</td>
                                        <td className="pr-2">{l.taxable ? `${Number(l.taxRatePercent)}%` : 'not taxed'}</td>
                                        <td className="pr-2">${Number(l.taxAmount).toFixed(2)}</td>
                                        <td className="pr-2">{String(l.taxPayer).toLowerCase()}</td>
                                        <td className="pr-2">{String(l.taxRemittedBy).toLowerCase()}</td>
                                        <td>{[l.province, l.country].filter(Boolean).join(', ')}{l.jurisdictionLabel ? ` · ${l.jurisdictionLabel}` : ''}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Total tax on this booking: ${(selectedTask as any).taxLedger.reduce((s: number, l: any) => s + Number(l.taxAmount || 0), 0).toFixed(2)}</p>
                            </div>
                          )}
                          {/* Once Stripe's exact fee is recorded the FINAL net replaces the estimate. */}
                          <DetailRow
                            label={selectedTask.stripeFeeRecordedAt && selectedTask.taskerNetFinal != null ? 'Expert Earned (final)' : 'Expert Earned (estimate)'}
                            value={`$${(selectedTask.stripeFeeRecordedAt && selectedTask.taskerNetFinal != null ? selectedTask.taskerNetFinal : selectedTask.taskerPayout).toFixed(2)}`} />

                          {/* Where the customer's payment went. The four always add up:
                              Customer paid = Stripe + Expert + Platform. */}
                          {selectedTask.totalCharged != null && (() => {
                            const pf = (selectedTask.processingFee ?? 0) + (selectedTask.processingFeeTasker ?? 0)
                            const isFinal = !!selectedTask.stripeFeeRecordedAt && selectedTask.taskerNetFinal != null && selectedTask.stripeFeeActual != null
                            // `?? 0` keeps the type checker happy (narrowing from the checks above is
                            // lost inside this closure) and never changes a real value.
                            const total  = selectedTask.totalCharged ?? 0
                            const payout = selectedTask.taskerPayout ?? 0
                            const stripe = (isFinal ? selectedTask.stripeFeeActual : pf) ?? 0
                            const expert = (isFinal ? selectedTask.taskerNetFinal : selectedTask.taskerPayout) ?? 0
                            const app = round2(total - payout - pf)
                            const tax = round2((selectedTask.taxAmount ?? 0) + (selectedTask.taxFeeTasker ?? 0))
                            return (
                              <>
                                <p className="text-xs text-gray-500 uppercase mt-3 mb-2">Where the money went</p>
                                <DetailRow label="Customer paid" value={`$${total.toFixed(2)}`} />
                                <DetailRow label={isFinal ? 'Stripe took' : 'Stripe took (estimate)'} value={`$${round2(stripe).toFixed(2)}`} />
                                <DetailRow label={isFinal ? 'Expert took (final)' : 'Expert took (estimate)'} value={`$${round2(expert).toFixed(2)}`} />
                                <DetailRow label="Platform took" value={`$${app.toFixed(2)}`} />
                                <DetailRow label="  of which tax to remit" value={`$${tax.toFixed(2)}`} />
                                <DetailRow label="  MatchnBook keeps" value={`$${round2(app - tax).toFixed(2)}`} />
                              </>
                            )
                          })()}
                        </>
                      ) : (
                        <p className="text-xs text-gray-600">Not available — this task was hired before per-job payout tracking was added.</p>
                      )}
                    </>
                  )}
                </div>

                {negotiation && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 uppercase mb-2">Negotiation History</p>
                    {negotiation.agreedPrice != null && (
                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2 mb-2">
                        <p className="text-xs text-green-400">
                          ✅ Agreed: ${negotiation.agreedPrice.toFixed(2)} {negotiation.agreedPriceType === 'hourly' ? '/ hour' : 'total'}
                        </p>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {negotiation.offers.map((o) => (
                        <div key={o.id} className="flex justify-between items-center text-xs bg-surface-tertiary rounded-lg px-3 py-2">
                          <span className="text-gray-400 capitalize">{o.offeredByRole}</span>
                          <span className="text-gray-300">${o.amount.toFixed(2)} {o.offerType === 'hourly' ? '/hr' : 'total'}</span>
                          <span className={`px-2 py-0.5 rounded-full ${
                            o.status === 'accepted' ? 'bg-green-600/20 text-green-400' :
                            o.status === 'rejected' ? 'bg-red-600/20 text-red-400' :
                            o.status === 'pending' ? 'bg-yellow-600/20 text-yellow-400' : 'bg-gray-600/20 text-gray-400'
                          }`}>{o.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {sessionNotes.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 uppercase mb-2">Session Notes</p>
                    <div className="space-y-2">
                      {sessionNotes.map((note) => (
                        <div key={note.id} className="bg-surface-tertiary border border-border-light rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-500 mb-1">
                            {new Date(note.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          <p className="text-xs text-gray-300 whitespace-pre-wrap">{note.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase mb-2">Timing</p>
                  <DetailRow label="Posted" value={formatDate(selectedTask.createdAt)} />
                  <DetailRow label="Hired At" value={formatDate(selectedTask.hiredAt)} />
                  <DetailRow label="Last Updated" value={formatDate(selectedTask.updatedAt)} />
                  {selectedTask.cancellationReason && (
                    <DetailRow label="Cancelled because" value={CANCELLATION_REASON_LABELS[selectedTask.cancellationReason] || selectedTask.cancellationReason} />
                  )}
                </div>

                {/* Disputed (completion dispute, or a no-show sent to review):
                    the three decisions. Reloads the task and the list after. */}
                {selectedTask.status === 'disputed' && (
                  <DisputeResolvePanel
                    taskId={selectedTask.id}
                    disputeReason={selectedTask.disputeReason}
                    canResolve={canResolveDisputes}
                    paymentStatus={selectedTask.paymentStatus}
                    hiredAt={selectedTask.hiredAt}
                    onResolved={() => { openDetail(selectedTask.id); fetchTasks() }}
                  />
                )}

                {/* Active job: support can mark it done for a tasker who
                    forgot or couldn't — the customer still confirms. */}
                {selectedTask.status === 'assigned' && (
                  <AdminMarkDonePanel
                    key={selectedTask.id}
                    taskId={selectedTask.id}
                    canAct={canResolveDisputes}
                    onDone={() => { openDetail(selectedTask.id); fetchTasks() }}
                  />
                )}

                {/* Every recorded action on this task — who, when, and for
                    arrivals where (distance to the address, verified, map). */}
                <TimelineSection
                  title="Activity Timeline"
                  fetcher={() => getTaskTimeline(selectedTask.id)}
                  reloadKey={`${selectedTask.id}-${selectedTask.status}`}
                />

                {isSuperAdmin && (
                  <div className="flex gap-2 pt-2 border-t border-border-light">
                    <button onClick={startEdit} className="flex-1 px-4 py-2 text-sm bg-brand-600/10 text-brand-400 rounded-xl hover:bg-brand-600/20 transition-colors">Edit</button>
                    <button onClick={handleDeleteTask} disabled={deleting} className="flex-1 px-4 py-2 text-sm bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 disabled:opacity-50 transition-colors">
                      {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* In-page photo viewer — same image the thumbnail already loaded,
          so it shows even after the 5-minute link has expired. For a
          fresh link, close the task and open it again. */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewPhoto(null)}>
          <div className="max-w-3xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img src={previewPhoto.fileUrl} alt={previewPhoto.fileName} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500 truncate pr-4">{previewPhoto.fileName}</p>
              <button onClick={() => setPreviewPhoto(null)} className="text-sm text-gray-400 hover:text-white">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const CANCELLATION_REASON_LABELS: Record<string, string> = {
  customer_cancelled: 'Customer cancelled',
  tasker_cancelled:   'Tasker cancelled',
  tasker_no_show:     'Tasker no-show',
  customer_no_show:   'Customer no-show',
  no_attendance:      'Nobody checked in (auto-settled)',
  admin:              'Admin',
}

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b border-border-light last:border-0">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm text-gray-300 text-right">{value}</p>
  </div>
)

export default TasksPage